import { toastManager } from "../components/ui/toast";
import { NEEDS_THE_MAC_KIND } from "../webHost";
import { normalizeStoredWorkroomBoardAddress } from "../workroomStorage";
// RM9 (register RM-CAN-09): a World session's deck is Actor-in-World VIEW
// state held by the World authority, keyed by (worldKey, sessionId), through
// the view-prefs door. This module is the one seam between the deck store and
// that door: hydrate every session's deck when a World's index is read back,
// and save a session's deck (debounced) when it changes. Browser storage keeps
// only per-device conveniences; a World session's deck never persists there.
//
// "When it changes" is measured against the deck as the door last held it,
// never against store notifications: a reducer that rebuilds an identical
// slice, a reconcile that finds nothing to reconcile, or a re-serialized pin
// must all leave the door untouched. A quiet Desk writes nothing.
import {
  deckScopeKey,
  isRetiredBoardKind,
  validBoomgikBoard,
  useBoardDeckStore,
  type Board,
  type SingletonBoardKind,
  type ThreadBoardDeckState,
} from "../boardDeckStore";
import {
  readWorldViewPrefs,
  saveWorldSessionDeck,
  type SessionDeckBoardWire,
  type SessionDeckWire,
} from "./worldViewPrefsClient";

const WORLD_SESSION_PREFIX = "world-session:";

export function isWorldSessionDeckKey(threadKey: string): boolean {
  return threadKey.startsWith(WORLD_SESSION_PREFIX);
}

export function parseWorldSessionDeckKey(
  threadKey: string,
): { readonly worldKey: string; readonly sessionId: string } | null {
  if (!isWorldSessionDeckKey(threadKey)) return null;
  const [world, session] = threadKey.slice(WORLD_SESSION_PREFIX.length).split(":");
  if (!world || !session) return null;
  return { worldKey: decodeURIComponent(world), sessionId: decodeURIComponent(session) };
}

/** A session's deck as the door holds it, minus the session it belongs to. */
export interface DeckWire {
  readonly boards: ReadonlyArray<SessionDeckBoardWire>;
  readonly activeBoardId?: string;
}

/** The board's target: the one id its kind addresses, when it addresses one. */
function boardTarget(board: Board): string | undefined {
  if (board.kind === "boomgik" && "projectId" in board) return board.projectId;
  const b = board as unknown as Record<string, unknown>;
  for (const key of ["resourceId", "relativePath", "memberId", "sessionId", "number"]) {
    const value = b[key];
    if (typeof value === "string" && value.length > 0) return value;
    if (typeof value === "number") return String(value);
  }
  return undefined;
}

/**
 * The deck for the door. A pinned board's `openedAt` is the moment the pin
 * was TAKEN, not the moment the deck was last written: `prior` (the deck as
 * the door last held it) carries that moment forward while the pin stands on
 * the same revision, so re-serializing an unchanged deck yields an identical
 * wire. Only a new pin is stamped now.
 */
export function serializeDeck(state: ThreadBoardDeckState, prior?: DeckWire): DeckWire {
  const boards: SessionDeckBoardWire[] = state.surfaces.map((board) => {
    const { id, kind, ...rest } = board as unknown as { id: string; kind: string } & Record<
      string,
      unknown
    >;
    const target = boardTarget(board);
    const revisionKey =
      typeof rest["pinnedRevisionKey"] === "string"
        ? (rest["pinnedRevisionKey"] as string)
        : undefined;
    const payload = Object.keys(rest).length === 0 ? undefined : JSON.stringify(rest);
    const standing = prior?.boards.find((held) => held.id === id)?.pin;
    const openedAt =
      standing !== undefined && standing.revisionKey === revisionKey
        ? standing.openedAt
        : Date.now();
    return {
      id,
      kind,
      ...(target === undefined ? {} : { target }),
      ...(payload === undefined ? {} : { payload }),
      ...(revisionKey === undefined ? {} : { pin: { revisionKey, openedAt } }),
    };
  });
  const activeBoardId =
    state.activeSurfaceId !== null && boards.some((b) => b.id === state.activeSurfaceId)
      ? state.activeSurfaceId
      : undefined;
  return { boards, ...(activeBoardId === undefined ? {} : { activeBoardId }) };
}

export function deserializeDeck(deck: SessionDeckWire): ThreadBoardDeckState {
  const surfaces = deck.boards.flatMap<Board>((wire) => {
    if (isRetiredBoardKind(wire.kind)) return [];
    let rest: Record<string, unknown> = {};
    if (wire.payload !== undefined) {
      try {
        const parsed: unknown = JSON.parse(wire.payload);
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed))
          rest = parsed as Record<string, unknown>;
      } catch {
        rest = {};
      }
    }
    // The member door's target is the address. Cleo need not duplicate it
    // inside the UI's serialized payload, and payload cannot override it.
    const board = {
      ...rest,
      id: normalizeStoredWorkroomBoardAddress(wire.id),
      kind: normalizeStoredWorkroomBoardAddress(wire.kind),
      ...(wire.kind === "sub-session" && wire.target !== undefined
        ? { sessionId: wire.target }
        : {}),
    };
    if (
      wire.kind === "boomgik" &&
      (!validBoomgikBoard(board) ||
        (wire.id === "boomgik" ? wire.target !== undefined : wire.target !== rest["projectId"]))
    )
      return [];
    return [board as unknown as Board];
  });
  const activeBoardId =
    deck.activeBoardId === undefined
      ? undefined
      : normalizeStoredWorkroomBoardAddress(deck.activeBoardId);
  const uniqueSurfaces = surfaces.filter(
    (board, index) =>
      board.kind !== "workrooms" || surfaces.findIndex((other) => other.id === board.id) === index,
  );
  return {
    isOpen: uniqueSurfaces.length > 0,
    activeSurfaceId: uniqueSurfaces.some((board) => board.id === activeBoardId)
      ? activeBoardId!
      : (uniqueSurfaces[0]?.id ?? null),
    surfaces: uniqueSurfaces,
    canvasInitialized: true,
  };
}

/** The "nothing changed" baseline per session key: the deck last sent, or last confirmed after a failure. */
const lastWire = new Map<string, { readonly stamp: string; readonly wire: DeckWire }>();

/**
 * The deck the door last CONFIRMED it holds for a session (a read, or a save
 * it answered ok), apart from requests still in flight. `seq` orders it
 * against saves so a late answer to an older save never replaces it.
 */
const confirmed = new Map<
  string,
  { readonly seq: number; readonly stamp: string; readonly wire: DeckWire }
>();
/** One save request per debounced change, numbered in the order it was made. */
interface SaveRequest {
  readonly seq: number;
  /** The door refused it (or could not be reached). */
  failed: boolean;
}
/** The newest save requested per session (in flight, or waiting on the one in flight). */
const newest = new Map<string, SaveRequest>();
let saveSeq = 0;

function remember(threadKey: string, wire: DeckWire): void {
  const stamp = JSON.stringify(wire);
  lastWire.set(threadKey, { stamp, wire });
  confirmed.set(threadKey, { seq: saveSeq, stamp, wire });
}

/** The baseline returns to the last confirmed deck, so the next change saves. */
function fallBackToConfirmed(threadKey: string): void {
  const held = confirmed.get(threadKey);
  if (held === undefined) lastWire.delete(threadKey);
  else lastWire.set(threadKey, { stamp: held.stamp, wire: held.wire });
}

/**
 * How long a door's request to front a board (see requestDeckFront) waits for
 * the session's saved deck. The deck it waits for is one or two view-prefs
 * reads (the session's own, and the World's, started with the room index):
 * normally well under a second, and 5 s still covers a slow local server.
 * Past that, a restore that lands belongs to a later moment (a Retry, a
 * return to the room) and must bring back the last tab, not a stale press.
 */
export const DECK_FRONT_REQUEST_WINDOW_MS = 5_000;

/**
 * The one standing request that a session's deck front a board over whatever
 * saved deck is restored for it next (the rail player's expand: the Music
 * room on its main tab). The deck layer applies it wherever a restored deck is
 * seated for that session, from any read. It is the newest door's alone, and
 * lapses when the person goes to any other session (or out of the World) or
 * after DECK_FRONT_REQUEST_WINDOW_MS.
 */
type MemberFrontTarget = Parameters<ReturnType<typeof useBoardDeckStore.getState>["openMember"]>[1];
type DeckFrontRequest = {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly requestedAt: number;
} & (
  | { readonly kind: SingletonBoardKind; readonly member?: never }
  | { readonly kind?: never; readonly member: MemberFrontTarget }
);
let frontRequest: DeckFrontRequest | null = null;

type SessionRef = { readonly worldKey: string; readonly sessionId: string };

/** A door asks this session's deck to front `kind`, now and over its next restore. */
export function requestDeckFront(ref: SessionRef, kind: SingletonBoardKind): void {
  frontRequest = {
    worldKey: ref.worldKey,
    sessionId: ref.sessionId,
    kind,
    requestedAt: Date.now(),
  };
  useBoardDeckStore.getState().open(ref, kind);
}

/** A saved Canvas/member front uses the same restore, expiry and session fences as a builtin board. */
export function requestMemberDeckFront(ref: SessionRef, member: MemberFrontTarget): void {
  frontRequest = { ...ref, member, requestedAt: Date.now() };
  useBoardDeckStore.getState().openMember(ref, member);
}

/** True while the door's request that this session's deck front `kind` still stands. */
export function deckFrontStands(ref: SessionRef, kind: SingletonBoardKind): boolean {
  const request = liveFrontRequest();
  return (
    request !== null &&
    request.worldKey === ref.worldKey &&
    request.sessionId === ref.sessionId &&
    request.kind === kind
  );
}

/** A newer door asked for nothing: no earlier request stands. */
export function withdrawDeckFront(): void {
  frontRequest = null;
}

/** The person is now on `ref` (null: no World session): a request for any other lapses. */
export function keepDeckFrontOnlyFor(ref: SessionRef | null): void {
  if (
    frontRequest !== null &&
    (ref === null ||
      frontRequest.worldKey !== ref.worldKey ||
      frontRequest.sessionId !== ref.sessionId)
  )
    frontRequest = null;
}

function liveFrontRequest(): DeckFrontRequest | null {
  if (frontRequest !== null && Date.now() - frontRequest.requestedAt > DECK_FRONT_REQUEST_WINDOW_MS)
    frontRequest = null;
  return frontRequest;
}

/**
 * Reads are ordered by when they started. A read seats a session's deck only
 * if no read that started later has already answered for that session: a
 * slow whole-World read must not put back an older deck over the session's
 * own newer restore (and cancel the save of what was fronted since).
 */
let readSeq = 0;
const answeredBy = new Map<string, number>();

function answer(threadKey: string, seq: number): boolean {
  if ((answeredBy.get(threadKey) ?? 0) > seq) return false;
  answeredBy.set(threadKey, seq);
  return true;
}

/** Read the World's decks through the door and seat them in the store. */
export async function hydrateWorldSessionDecks(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
  sessionId?: string,
  stillCurrent: () => boolean = () => true,
): Promise<boolean> {
  const seq = ++readSeq;
  const outcome = await readWorldViewPrefs(worldKey, fetchImpl, sessionId);
  if (!outcome.ok || !stillCurrent()) return false;
  // An exact successful read with no row means this Actor has no saved deck
  // yet. A new workroom's opening board may still be waiting on its first
  // debounced save; leave that local deck and its pending write untouched.
  if (
    sessionId !== undefined &&
    !outcome.sessionDecks.some((deck) => deck.sessionId === sessionId)
  ) {
    answer(deckScopeKey({ worldKey, sessionId }), seq);
    return true;
  }
  const decks = outcome.sessionDecks.filter(
    (deck) =>
      (sessionId === undefined || deck.sessionId === sessionId) &&
      answer(deckScopeKey({ worldKey, sessionId: deck.sessionId }), seq),
  );
  const entries = decks.map((deck) => {
    const key = deckScopeKey({ worldKey, sessionId: deck.sessionId });
    const state = deserializeDeck(deck);
    cancelPending(key);
    dropWaiting(key);
    remember(key, serializeDeck(state, deck));
    return [key, state] as const;
  });
  useBoardDeckStore.getState().hydrateSessionDecks(entries);
  const request = liveFrontRequest();
  if (
    request !== null &&
    request.worldKey === worldKey &&
    decks.some((deck) => deck.sessionId === request.sessionId)
  ) {
    if (request.member) useBoardDeckStore.getState().openMember(request, request.member);
    else useBoardDeckStore.getState().open(request, request.kind);
  }
  return true;
}

const SAVE_DELAY_MS = 400;
const pending = new Map<string, ReturnType<typeof setTimeout>>();
/** Session keys whose last save failed and whose member has been told. */
const warned = new Set<string>();
/**
 * One save in flight per session: a later deck waits for the earlier save's
 * answer, and only the newest waiting deck is sent, so the World never ends on
 * an older deck that arrived after a newer one. A waiting deck is already the
 * session's newest request (see `newest`), so the answer to the save in flight
 * is an older save's answer and decides nothing for it.
 */
interface WaitingSave {
  readonly request: SaveRequest;
  readonly stamp: string;
  readonly wire: DeckWire;
}
const inFlight = new Map<string, { next: WaitingSave | null }>();

function send(threadKey: string, worldKey: string, sessionId: string, save: WaitingSave): void {
  const flight = inFlight.get(threadKey);
  if (flight !== undefined) {
    flight.next = save;
    return;
  }
  const mine: { next: WaitingSave | null } = { next: null };
  inFlight.set(threadKey, mine);
  void saveWorldSessionDeck(worldKey, sessionId, save.wire)
    .then(
      (outcome) => settle(threadKey, save, outcome.ok ? { ok: true } : outcome),
      () => settle(threadKey, save, { ok: false, kind: "transport" }),
    )
    .finally(() => {
      if (inFlight.get(threadKey) !== mine) return;
      inFlight.delete(threadKey);
      if (mine.next !== null) send(threadKey, worldKey, sessionId, mine.next);
    });
}

/** A read seated this session's deck: a deck still waiting to be sent is older than it. */
function dropWaiting(threadKey: string): void {
  const flight = inFlight.get(threadKey);
  if (flight !== undefined) flight.next = null;
}

/** What the member is told once about a save the door did not take; the browser host's line names the Mac (webHost.ts). */
export function deckSaveRefusedWords(kind: string): string {
  return kind === NEEDS_THE_MAC_KIND
    ? "These Canvas tabs are kept in this window only; saving them to the World needs the Cleo Mac app."
    : "Couldn't confirm these tabs were saved; Cleo will try again on your next change.";
}

/** The door answered one save: update what is confirmed, and tell the member of a refusal. */
function settle(
  threadKey: string,
  save: WaitingSave,
  answer: { readonly ok: true } | { readonly ok: false; readonly kind: string },
): void {
  const { request, stamp, wire } = save;
  const latest = newest.get(threadKey);
  if (answer.ok) {
    if (request.seq > (confirmed.get(threadKey)?.seq ?? -1))
      confirmed.set(threadKey, { seq: request.seq, stamp, wire });
    if (latest === request) warned.delete(threadKey);
    // The newest save already failed: the door now holds this older one.
    else if (latest?.failed === true) fallBackToConfirmed(threadKey);
    return;
  }
  // An older save's failure says nothing about a newer one: that save's
  // own answer decides, and a newer success already holds the deck.
  if (latest !== request) return;
  request.failed = true;
  fallBackToConfirmed(threadKey);
  if (warned.has(threadKey)) return;
  warned.add(threadKey);
  toastManager.add({
    type: "warning",
    timeout: 10000,
    title: deckSaveRefusedWords(answer.kind),
  });
}

function cancelPending(threadKey: string): void {
  const timer = pending.get(threadKey);
  if (timer === undefined) return;
  clearTimeout(timer);
  pending.delete(threadKey);
}

/**
 * Save only a deck that differs from the door's last known one. A deck that
 * changed and changed back before the debounce fired is not a change either:
 * its pending save is dropped.
 *
 * A save the door does not take is never silent (W-80db70b8): when the
 * failed save is still the newest one requested, the baseline returns to the last
 * deck the door CONFIRMED (never another unconfirmed request's deck), so the
 * next change always saves again, and the member is told once. A failure that
 * a newer save has overtaken changes nothing and tells no one.
 */
function scheduleSave(threadKey: string, state: ThreadBoardDeckState): void {
  const coordinate = parseWorldSessionDeckKey(threadKey);
  if (coordinate === null) return;
  const known = lastWire.get(threadKey);
  const wire = serializeDeck(state, known?.wire);
  const stamp = JSON.stringify(wire);
  cancelPending(threadKey);
  if (known?.stamp === stamp) return;
  pending.set(
    threadKey,
    setTimeout(() => {
      pending.delete(threadKey);
      lastWire.set(threadKey, { stamp, wire });
      const request: SaveRequest = { seq: ++saveSeq, failed: false };
      newest.set(threadKey, request);
      send(threadKey, coordinate.worldKey, coordinate.sessionId, { request, stamp, wire });
    }, SAVE_DELAY_MS),
  );
}

/** Watch the store: a changed World session deck rides to the door, debounced. */
export function startSessionDeckSync(): () => void {
  let previous = useBoardDeckStore.getState().byThreadKey;
  const unsubscribe = useBoardDeckStore.subscribe((state) => {
    const next = state.byThreadKey;
    if (next === previous) return;
    for (const [threadKey, deck] of Object.entries(next)) {
      if (!isWorldSessionDeckKey(threadKey)) continue;
      if (previous[threadKey] === deck) continue;
      scheduleSave(threadKey, deck);
    }
    previous = next;
  });
  return () => {
    unsubscribe();
    for (const timer of pending.values()) clearTimeout(timer);
    pending.clear();
    inFlight.clear();
  };
}

/** Tests: forget every remembered wire so the next change saves. */
export function resetSessionDeckSync(): void {
  lastWire.clear();
  confirmed.clear();
  newest.clear();
  warned.clear();
  answeredBy.clear();
  inFlight.clear();
  frontRequest = null;
  for (const timer of pending.values()) clearTimeout(timer);
  pending.clear();
}
